'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChainId } from '@morpho-org/blue-sdk';
import { CursorPagination } from '@morpho-org/ui-v6/client';
import { TableNoResults } from '@morpho-org/ui-v6/server';
import { useTable } from '@tanstack/react-table';
import { useState } from 'react';
import { DateRangeFilter, dateRangeToTimestamps, type DateRangeValue } from '@/components/common/DateRangeFilter';
import { ErrorDataUnavailable } from '@/components/common/Error';
import { SubSectionTitle } from '@/components/common/SubSectionTitle';
import { Table, TrBodyRow, TrHeaderGroup } from '@/components/common/Table';
import { TableScrollArea } from '@/components/common/Table/CompactTable/Table';
import { useSkeletonRows } from '@/hooks/useSkeletonRows';
import { PAGE_SIZE } from './config';
import { columnLabelsLiquidationTable, columnsLiquidationTable, liquidationTableFeatures } from './config/LiquidationTable.columns';
import { useLiquidationTableData, useLiquidationTableRow } from './hooks';
export interface LiquidationTableClientProps {
  chainId: ChainId;
  marketId: string;
}
const LiquidationTableClient = t0 => {
  const $ = _c(53);
  const {
    chainId,
    marketId
  } = t0;
  const [dateRange, setDateRange] = useState();
  let t1;
  if ($[0] !== dateRange) {
    t1 = dateRangeToTimestamps(dateRange);
    $[0] = dateRange;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    timestampGte,
    timestampLte
  } = t1;
  let t2;
  if ($[2] !== chainId || $[3] !== marketId || $[4] !== timestampGte || $[5] !== timestampLte) {
    t2 = {
      chainId,
      marketId,
      first: PAGE_SIZE,
      timestampGte,
      timestampLte
    };
    $[2] = chainId;
    $[3] = marketId;
    $[4] = timestampGte;
    $[5] = timestampLte;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const {
    items: data,
    partial,
    isLoading,
    isLoadingNextPage,
    error,
    pageIndex,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    prefetchNext
  } = useLiquidationTableData(t2);
  let t3;
  if ($[7] !== chainId || $[8] !== data) {
    t3 = {
      chainId,
      transactions: data
    };
    $[7] = chainId;
    $[8] = data;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const liquidations = useLiquidationTableRow(t3);
  let t4;
  if ($[10] !== liquidations) {
    t4 = {
      features: liquidationTableFeatures,
      data: liquidations,
      columns: columnsLiquidationTable,
      enableSorting: false
    };
    $[10] = liquidations;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const liquidationTable = useTable(t4);
  const loading = isLoading || isLoadingNextPage;
  let t5;
  if ($[12] !== liquidationTable) {
    t5 = liquidationTable.getRowModel();
    $[12] = liquidationTable;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const emptyRows = t5.rows.length === 0;
  let t6;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      first: PAGE_SIZE,
      totalCount: PAGE_SIZE,
      pagination: {
        pageIndex: 0,
        pageSize: PAGE_SIZE
      },
      columnsDefs: columnsLiquidationTable
    };
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const skeletonRows = useSkeletonRows(t6);
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <SubSectionTitle>All Liquidations</SubSectionTitle>;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== dateRange) {
    t8 = <div className="max-lg:gap-xs flex flex-row items-center justify-between max-lg:flex-col max-lg:items-start max-lg:justify-start">{t7}<DateRangeFilter value={dateRange} onChange={setDateRange} side="bottom" align="end" page="market_liquidations" /></div>;
    $[16] = dateRange;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== emptyRows || $[19] !== liquidationTable || $[20] !== loading || $[21] !== skeletonRows) {
    t9 = (!emptyRows || loading) && <Table.Table tableVariant="compact"><Table.Thead>{liquidationTable.getHeaderGroups().map(_temp)}</Table.Thead><Table.Tbody>{!loading && liquidationTable.getRowModel().rows.map(_temp2)}{loading && skeletonRows.map(_temp3)}</Table.Tbody></Table.Table>;
    $[18] = emptyRows;
    $[19] = liquidationTable;
    $[20] = loading;
    $[21] = skeletonRows;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== dateRange || $[24] !== emptyRows || $[25] !== error || $[26] !== loading) {
    t10 = emptyRows && !loading && !error && <TableNoResults text={dateRange ? "No liquidations in the selected date range." : "No liquidation has been executed."} height="478px" style={{
      borderRadius: "0"
    }} />;
    $[23] = dateRange;
    $[24] = emptyRows;
    $[25] = error;
    $[26] = loading;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== error || $[29] !== loading) {
    t11 = !loading && error && <ErrorDataUnavailable minHeight="478px" style={{
      borderRadius: "0"
    }} />;
    $[28] = error;
    $[29] = loading;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== t10 || $[32] !== t11 || $[33] !== t9) {
    t12 = <TableScrollArea className="grid h-full w-full rounded-[20px] [&_td:first-of-type]:w-1/2 [&_td:nth-of-type(2)]:w-auto">{t9}{t10}{t11}</TableScrollArea>;
    $[31] = t10;
    $[32] = t11;
    $[33] = t9;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== error || $[36] !== loading || $[37] !== partial) {
    t13 = partial && !loading && !error && <p className="text-secondary-foreground px-sm text-xs" data-testid="liquidations-table-partial-notice">Some liquidations could not be loaded, so this page may be incomplete.</p>;
    $[35] = error;
    $[36] = loading;
    $[37] = partial;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== canNextPage || $[40] !== canPreviousPage || $[41] !== error || $[42] !== loading || $[43] !== nextPage || $[44] !== pageIndex || $[45] !== prefetchNext || $[46] !== previousPage) {
    t14 = <CursorPagination loading={loading} error={error} pageIndex={pageIndex} previousPage={previousPage} nextPage={nextPage} canPreviousPage={canPreviousPage} canNextPage={canNextPage} onPrefetchNext={prefetchNext} />;
    $[39] = canNextPage;
    $[40] = canPreviousPage;
    $[41] = error;
    $[42] = loading;
    $[43] = nextPage;
    $[44] = pageIndex;
    $[45] = prefetchNext;
    $[46] = previousPage;
    $[47] = t14;
  } else {
    t14 = $[47];
  }
  let t15;
  if ($[48] !== t12 || $[49] !== t13 || $[50] !== t14 || $[51] !== t8) {
    t15 = <div className="gap-xs flex h-full w-full flex-col">{t8}{t12}{t13}{t14}</div>;
    $[48] = t12;
    $[49] = t13;
    $[50] = t14;
    $[51] = t8;
    $[52] = t15;
  } else {
    t15 = $[52];
  }
  return t15;
};
export default LiquidationTableClient;
function _temp(headerGroup) {
  return <TrHeaderGroup key={headerGroup.id} columnLabels={columnLabelsLiquidationTable} headerGroup={headerGroup} />;
}
function _temp2(row) {
  return <TrBodyRow key={row.id} row={row} />;
}
function _temp3(row_0, index) {
  return <TrBodyRow key={index + "skeleton-table-liquidation"} skeletonRow={row_0} />;
}